import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/quote-confirm`;

const send = async (t, answer) => {
  const r = await fetch(API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ t, answer }) });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Something went wrong");
  return (await r.json()).status;
};

// Opened from the confirmation email: the button they clicked is applied right away.
export default function QuoteConfirm() {
  const [params] = useSearchParams();
  const t = params.get("t") || "";
  const [quote, setQuote] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const answer = params.get("a") === "yes" ? "yes" : params.get("a") === "no" ? "no" : null;
    fetch(`${API}?t=${encodeURIComponent(t)}`)
      .then(async (r) => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "This link isn't valid."); return r.json(); })
      .then(async (d) => { setQuote(d); setStatus(answer ? await send(t, answer) : d.status); })
      .catch((e) => setError(e.message));
  }, [t, params]);

  const change = async (answer) => {
    setBusy(true);
    try { setStatus(await send(t, answer)); } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const yes = status === "approved";
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="quote-confirm-page">
      <header className="mx-auto max-w-3xl px-6 h-16 flex items-center"><Logo /></header>
      <main className="mx-auto max-w-xl px-6 py-16">
        {error ? <p data-testid="quote-confirm-error" className="text-[#FF5C7A]">{error}</p>
          : !quote || !status ? <p className="text-[#A3A3A3] flex items-center gap-2"><CircleNotch className="animate-spin" /> Saving your answer…</p>
          : (
            <>
              {yes ? <CheckCircle size={36} className="text-[#00FF9D]" /> : <XCircle size={36} className="text-[#A3A3A3]" />}
              <h1 data-testid="quote-confirm-title" className="font-display text-3xl mt-4">{yes ? "Thank you! You've said yes." : status === "declined" ? "Got it. We won't use your quote." : "Your answer is needed"}</h1>
              <p className="mt-3 text-sm text-[#A3A3A3]">{yes ? "Sonia will add it to the website shortly. Changed your mind? You can still say no below." : "Nothing about your feedback will appear on the website."}</p>
              <blockquote data-testid="quote-confirm-quote" className="mt-8 border-l-2 border-[#00FF9D] pl-4 leading-relaxed">“{quote.quote}”</blockquote>
              <p className="mt-2 text-xs text-[#A3A3A3]">{[quote.name, quote.role].filter(Boolean).join(" · ") || "Shown as: CalibRunway customer"}</p>
              <button data-testid={yes ? "quote-confirm-no" : "quote-confirm-yes"} disabled={busy} onClick={() => change(yes ? "no" : "yes")}
                className="mt-8 rounded-full border border-[#2A2A2A] px-5 py-2.5 text-sm hover:border-white transition-colors disabled:opacity-50">
                {busy ? "Saving…" : yes ? "No, please don't use it" : "Actually, yes, you can use it"}
              </button>
            </>
          )}
      </main>
    </div>
  );
}
